import type { MediaQualityTrack, SessionMediaQuality } from '@era/shared'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDuration } from '@/lib/format'
import type { SpeakerRoster } from '../speakers'

export function MediaQualityPanel({
  quality,
  roster,
}: {
  quality: SessionMediaQuality | null
  roster: SpeakerRoster
}) {
  if (!quality) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Browser media quality</CardTitle>
          <CardDescription>
            This retained snapshot predates per-connection browser WebRTC reporting.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const empty = quality.connections.length === 0
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Browser media quality</CardTitle>
            <CardDescription>
              Derived 5-second WebRTC samples reported by each browser. They describe what a
              browser sent or received, not recorder integrity and not proof that sound reached a
              person&rsquo;s physical speakers. “Browser only” attribution is non-authoritative;
              “server verified” means the controller independently matched a durable track row.
            </CardDescription>
          </div>
          <span className="rounded-full border border-line bg-paper px-3 py-1 text-xs text-ink-muted">
            {quality.status} · {quality.coverage_pct == null ? 'coverage unknown' : `${round(quality.coverage_pct)}% coverage`}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Fact label="Observers" value={`${quality.reported_observers}/${quality.expected_observers}`} />
          <Fact label="Connections" value={String(quality.connection_count)} />
          <Fact label="Observed" value={formatDuration(quality.observed_ms)} />
          <Fact label="Expected" value={formatDuration(quality.expected_observer_ms)} />
        </div>

        {empty ? (
          <p className="rounded-lg border border-dashed border-line px-4 py-5 text-sm text-ink-muted">
            {quality.status === 'collecting'
              ? 'No browser has uploaded its first cumulative snapshot yet.'
              : 'No browser quality report was retained for this call.'}
          </p>
        ) : (
          <div className="space-y-4">
            {quality.connections.map((connection) => (
              <section key={connection.connection_id} className="rounded-lg border border-line">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">
                      Observer {roster.get(connection.observer_identity).displayName}
                    </p>
                    <p className="text-xs text-ink-dim">
                      {formatDuration(connection.observed_duration_ms)} observed · sequence {connection.sequence}
                      {connection.final ? ' · exit/final signal seen' : ''}
                    </p>
                  </div>
                  <span className="font-mono text-[0.6875rem] text-ink-dim">
                    {shortId(connection.connection_id)}
                  </span>
                </div>
                <div className="divide-y divide-line">
                  {connection.tracks.map((track) => (
                    <TrackRow
                      key={`${track.direction}:${track.track_sid}`}
                      observer={roster.get(connection.observer_identity).displayName}
                      subject={roster.get(track.subject_identity).displayName}
                      track={track}
                    />
                  ))}
                  {connection.tracks.length === 0 ? (
                    <p className="px-4 py-4 text-sm text-ink-muted">
                      The browser reported a connection but no published or subscribed media track.
                    </p>
                  ) : null}
                </div>
              </section>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TrackRow({
  observer,
  subject,
  track,
}: {
  observer: string
  subject: string
  track: MediaQualityTrack
}) {
  const attribution = attributionPresentation(track.attribution_status)
  const media = track.kind === 'video'
    ? [
        metric(track.direction === 'inbound' ? 'Receive size' : 'Send size', resolution(track)),
        metric('FPS', value(track.fps_avg, ' fps')),
        metric('Freezes', freeze(track)),
      ]
    : [
        metric('Concealment', value(track.concealment_pct, '%')),
        metric('Jitter buffer', value(track.jitter_buffer_delay_ms_avg, ' ms')),
      ]
  const network = [
    metric('Bitrate', value(track.bitrate_kbps_avg, ' kbps')),
    metric('Loss', value(track.packet_loss_pct, '%')),
    metric('Jitter', value(track.jitter_ms_avg, ' ms')),
    metric('RTT', value(track.rtt_ms_avg, ' ms')),
    ...media,
    metric('Playback stalls', `${track.playback_stalls} · ${formatDuration(track.playback_stall_duration_ms)}`),
    metric('Playback errors', String(track.playback_errors)),
  ]

  return (
    <div className="space-y-3 px-4 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">
          {observer} <span className="text-ink-dim">→</span> {subject}
        </p>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span
            title={attribution.detail}
            className={attribution.className}
          >
            {attribution.label}
          </span>
          <p className="text-xs text-ink-dim">
            {track.direction} {track.source.replaceAll('_', ' ')} · {track.codec ?? track.kind} · {track.sample_count} samples
          </p>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        {network.map((item) => (
          <div key={item.label}>
            <dt className="text-[0.6875rem] uppercase tracking-wide text-ink-dim">{item.label}</dt>
            <dd className="mt-0.5 text-sm tabular text-ink-muted">{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-paper px-3 py-2.5">
      <p className="text-[0.6875rem] uppercase tracking-wide text-ink-dim">{label}</p>
      <p className="mt-0.5 text-sm font-medium tabular">{value}</p>
    </div>
  )
}

function metric(label: string, value: string) {
  return { label, value }
}

function resolution(track: MediaQualityTrack): string {
  if (track.frame_width_avg == null || track.frame_height_avg == null) return '—'
  return `${Math.round(track.frame_width_avg)}×${Math.round(track.frame_height_avg)}`
}

function freeze(track: MediaQualityTrack): string {
  if (track.freeze_count == null) return '—'
  return `${track.freeze_count} · ${formatDuration(track.freeze_duration_ms)}`
}

function value(input: number | null | undefined, suffix: string): string {
  return input == null ? '—' : `${round(input)}${suffix}`
}

function round(value: number): number {
  return Math.round(value * 10) / 10
}

function shortId(value: string): string {
  return value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value
}

function attributionPresentation(status: MediaQualityTrack['attribution_status']): {
  label: string
  detail: string
  className: string
} {
  const base = 'rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide'
  if (status === 'server_verified') {
    return {
      label: 'server verified',
      detail: 'The controller independently matched this report to its durable track catalog.',
      className: `${base} border-emerald-300/40 bg-emerald-50 text-emerald-800`,
    }
  }
  if (status === 'server_conflict') {
    return {
      label: 'server conflict',
      detail: 'Durable server evidence disagrees with the browser-reported subject, kind, or source.',
      className: `${base} border-red-300/50 bg-red-50 text-red-800`,
    }
  }
  return {
    label: 'browser only',
    detail: 'No durable server track row independently verifies this browser attribution.',
    className: `${base} border-line bg-paper text-ink-dim`,
  }
}
